export type ThemeColorInputType = 'color' | 'text'

export interface ThemeColorLabel {
  ar: string
  en: string
  fr: string
}

export interface ThemeColorItem {
  key: string
  label: ThemeColorLabel
  description?: ThemeColorLabel
  inputType?: ThemeColorInputType
  placeholder?: string
}

export interface ThemeColorSection {
  id: string
  title: ThemeColorLabel
  description?: ThemeColorLabel
  items: ThemeColorItem[]
}

export const THEME_COLOR_SECTIONS: ThemeColorSection[] = [
  {
    id: 'base',
    title: {
      ar: 'الألوان الأساسية',
      en: 'Base Colors',
      fr: 'Couleurs de Base'
    },
    description: {
      ar: 'ألوان الخلفيات والنصوص والحدود العامة في الواجهة',
      en: 'Background, text, and border colors used across the UI',
      fr: 'Couleurs de fond, texte et bordures utilisées dans l’interface'
    },
    items: [
      {
        key: '--bg-primary',
        label: { ar: 'الخلفية الرئيسية', en: 'Primary Background', fr: 'Fond Principal' },
        inputType: 'color'
      },
      {
        key: '--bg-secondary',
        label: { ar: 'الخلفية الثانوية', en: 'Secondary Background', fr: 'Fond Secondaire' },
        inputType: 'color'
      },
      {
        key: '--bg-tertiary',
        label: { ar: 'الخلفية المساعدة', en: 'Tertiary Background', fr: 'Fond Tertiaire' },
        inputType: 'color'
      },
      {
        key: '--text-primary',
        label: { ar: 'لون النص الرئيسي', en: 'Primary Text', fr: 'Texte Principal' },
        inputType: 'color'
      },
      {
        key: '--text-secondary',
        label: { ar: 'لون النص الثانوي', en: 'Secondary Text', fr: 'Texte Secondaire' },
        inputType: 'color'
      },
      {
        key: '--text-tertiary',
        label: { ar: 'لون النص المساعد', en: 'Tertiary Text', fr: 'Texte Tertiaire' },
        inputType: 'color'
      },
      {
        key: '--border-color',
        label: { ar: 'لون الحدود', en: 'Border Color', fr: 'Couleur des Bordures' },
        inputType: 'color'
      },
      {
        key: '--accent-color',
        label: { ar: 'لون التميز الأساسي', en: 'Primary Accent Color', fr: 'Couleur d’Accentuation' },
        description: { ar: 'اللون الرئيسي للأزرار والروابط والعناصر النشطة', en: 'Primary color for buttons, links, and active elements', fr: 'Couleur principale pour les boutons, liens et éléments actifs' },
        inputType: 'color'
      },
      {
        key: '--accent-color-hover',
        label: { ar: 'لون التميز عند التمرير', en: 'Accent Color Hover', fr: 'Accentuation au Survol' },
        inputType: 'color'
      }
    ]
  },
  {
    id: 'headerSidebar',
    title: {
      ar: 'الهيدر والقائمة الجانبية',
      en: 'Header & Sidebar',
      fr: 'En-tête et Menu Latéral'
    },
    description: {
      ar: 'تحكم بألوان الهيدر والقائمة الجانبية وحالة العناصر النشطة',
      en: 'Control header and sidebar colors including active items',
      fr: 'Contrôlez les couleurs de l’en-tête, du menu latéral et des éléments actifs'
    },
    items: [
      {
        key: '--header-bg',
        label: { ar: 'خلفية الهيدر', en: 'Header Background', fr: 'Fond de l’En-tête' },
        inputType: 'color'
      },
      {
        key: '--header-text',
        label: { ar: 'نص الهيدر', en: 'Header Text', fr: 'Texte de l’En-tête' },
        inputType: 'color'
      },
      {
        key: '--sidebar-bg',
        label: { ar: 'خلفية القائمة الجانبية', en: 'Sidebar Background', fr: 'Fond du Menu Latéral' },
        inputType: 'color'
      },
      {
        key: '--sidebar-text',
        label: { ar: 'نص القائمة الجانبية', en: 'Sidebar Text', fr: 'Texte du Menu Latéral' },
        inputType: 'color'
      },
      {
        key: '--sidebar-hover-bg',
        label: { ar: 'خلفية التمرير بالقائمة', en: 'Sidebar Hover Background', fr: 'Fond au Survol' },
        inputType: 'text',
        placeholder: 'rgba(255, 255, 255, 0.1)'
      },
      {
        key: '--primary-600',
        label: { ar: 'لون العنصر النشط', en: 'Active Item Color', fr: 'Couleur de l’élément actif' },
        inputType: 'color'
      }
    ]
  },
  {
    id: 'surfaces',
    title: {
      ar: 'البطاقات والحقول',
      en: 'Cards & Inputs',
      fr: 'Cartes et Champs'
    },
    description: {
      ar: 'ألوان البطاقات والحقول ومساحات التفاعل',
      en: 'Card and input colors with hover and shadow settings',
      fr: 'Couleurs des cartes et champs, avec survol et ombres'
    },
    items: [
      {
        key: '--card-bg',
        label: { ar: 'خلفية البطاقات', en: 'Card Background', fr: 'Fond des Cartes' },
        inputType: 'color'
      },
      {
        key: '--card-border',
        label: { ar: 'حدود البطاقات', en: 'Card Border', fr: 'Bordure des Cartes' },
        inputType: 'color'
      },
      {
        key: '--input-bg',
        label: { ar: 'خلفية الحقول', en: 'Input Background', fr: 'Fond des Champs' },
        inputType: 'color'
      },
      {
        key: '--input-border',
        label: { ar: 'حدود الحقول', en: 'Input Border', fr: 'Bordure des Champs' },
        inputType: 'color'
      },
      {
        key: '--hover-bg',
        label: { ar: 'خلفية التمرير', en: 'Hover Background', fr: 'Fond au Survol' },
        inputType: 'text',
        placeholder: '#f3f4f6'
      },
      {
        key: '--shadow',
        label: { ar: 'لون الظلال', en: 'Shadow Color', fr: 'Couleur de l’Ombre' },
        inputType: 'text',
        placeholder: 'rgba(0, 0, 0, 0.1)'
      }
    ]
  }
]

export const THEME_COLOR_KEYS = THEME_COLOR_SECTIONS.flatMap((section) =>
  section.items.map((item) => item.key)
)

export const THEME_COLOR_DEFAULTS: Record<string, string> = {
  '--primary-600': '#9333ea'
}

